import React, { useCallback, useEffect, useState } from 'react';
import { FaArrowLeft, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';
import { createCompromisoPago, getGestionMoras } from '../../../js/asesorGestionMorasService';
import CompromisoPagoModal from './CompromisoPagoModal';
import {
  CuotasVencidasTable,
  GestionMorasEmptyState,
  GestionMorasErrorState,
  GestionMorasMetricCards,
  GestionMorasTable,
  PrestamoClienteCell,
  PrestamoCurrencyCell,
  PrestamoNumeroCreditoCell,
  UltimoCompromisoCard
} from '../../../components/ui/GestionMorasComponents/GestionMorasShared';
import useCompromisoPagoForm from '../../../components/ui/GestionMorasComponents/useCompromisoPagoForm';
import {
  crearFormularioCompromiso,
  formatearFecha,
  obtenerFechaActualISO,
  obtenerRestriccionNuevoCompromiso,
  validarCompromisoForm
} from '../../../utilities/gestionMorasUtils';

const PRESTAMO_HEADERS = [
  { label: 'Cliente', className: 'px-4 py-4 text-left' },
  { label: 'N° Crédito', className: 'px-4 py-4 text-center' },
  { label: 'Capital pendiente', className: 'px-4 py-4 text-center' },
  { label: 'Total mora', className: 'px-4 py-4 text-center' },
  { label: 'Total a pagar', className: 'px-4 py-4 text-center' },
  { label: 'Compromiso', className: 'px-4 py-4 text-center' }
];

const GestionMorasUI = () => {
  const [prestamos, setPrestamos] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPrestamoId, setSelectedPrestamoId] = useState(null);
  const [prestamoCompromiso, setPrestamoCompromiso] = useState(null);
  const [savingCompromiso, setSavingCompromiso] = useState(false);
  const {
    formData: compromisoForm,
    errors: compromisoErrors,
    setErrors: setCompromisoErrors,
    resetForm: resetCompromisoForm,
    handleChange: handleCompromisoChange
  } = useCompromisoPagoForm(crearFormularioCompromiso);

  const aplicarGestionMoras = useCallback((data, selectedId = null) => {
    const nextPrestamos = Array.isArray(data.data) ? data.data : [];
    const nextSelectedId =
      selectedId !== null && nextPrestamos.some((prestamo) => prestamo.id_prestamo === selectedId)
        ? selectedId
        : null;

    setPrestamos(nextPrestamos);
    setMeta(data.meta || {});
    setSelectedPrestamoId(nextSelectedId);
  }, []);

  const cargarGestionMoras = useCallback(
    async (selectedId = null) => {
      try {
        setLoading(true);
        setError(null);

        const data = await getGestionMoras();
        aplicarGestionMoras(data, selectedId);
      } catch (err) {
        console.error('Error al cargar la gestión de moras:', err);
        setError(err.message || 'No se pudo cargar la gestión de moras');
        toast.error(err.message || 'Error al cargar la gestión de moras');
      } finally {
        setLoading(false);
      }
    },
    [aplicarGestionMoras]
  );

  useEffect(() => {
    cargarGestionMoras();
  }, [cargarGestionMoras]);

  const handleSeleccionarPrestamo = useCallback((prestamoId) => {
    setSelectedPrestamoId(prestamoId);
  }, []);

  const handleVolverAlListado = useCallback(() => {
    setSelectedPrestamoId(null);
  }, []);

  const limpiarCompromisoState = useCallback(() => {
    setPrestamoCompromiso(null);
    resetCompromisoForm();
  }, [resetCompromisoForm]);

  const abrirModalCompromiso = useCallback(
    (prestamo) => {
      const restriccionCompromiso = obtenerRestriccionNuevoCompromiso(prestamo);
      if (restriccionCompromiso.bloqueado) {
        toast.info(restriccionCompromiso.mensaje);
        return;
      }

      setPrestamoCompromiso(prestamo);
      resetCompromisoForm(prestamo);
    },
    [resetCompromisoForm]
  );

  const cerrarModalCompromiso = useCallback(() => {
    if (!savingCompromiso) {
      limpiarCompromisoState();
    }
  }, [limpiarCompromisoState, savingCompromiso]);

  const handleRegistrarCompromiso = useCallback(
    async (event) => {
      event.preventDefault();

      const restriccionCompromiso = obtenerRestriccionNuevoCompromiso(prestamoCompromiso);
      if (restriccionCompromiso.bloqueado) {
        toast.error(restriccionCompromiso.mensaje);
        return;
      }

      const nextErrors = validarCompromisoForm(compromisoForm, {
        minFechaCompromiso: obtenerFechaActualISO()
      });
      if (Object.keys(nextErrors).length > 0) {
        setCompromisoErrors(nextErrors);
        return;
      }

      const payload = {
        idPrestamo: Number(compromisoForm.idPrestamo),
        fecha_compromiso: compromisoForm.fecha_compromiso,
        monto_compromiso: Number.parseFloat(compromisoForm.monto_compromiso),
        motivo: compromisoForm.motivo.trim()
      };

      try {
        setSavingCompromiso(true);
        setCompromisoErrors({});

        await createCompromisoPago(payload);
        toast.success('Compromiso de pago registrado correctamente');

        limpiarCompromisoState();
        await cargarGestionMoras(payload.idPrestamo);
      } catch (err) {
        console.error('Error al registrar compromiso de pago:', err);
        toast.error(err.message || 'No se pudo registrar el compromiso de pago');
      } finally {
        setSavingCompromiso(false);
      }
    },
    [cargarGestionMoras, compromisoForm, limpiarCompromisoState, prestamoCompromiso, setCompromisoErrors]
  );

  const selectedPrestamo =
    selectedPrestamoId !== null
      ? prestamos.find((prestamo) => prestamo.id_prestamo === selectedPrestamoId) ?? null
      : null;

  const visiblePrestamos =
    selectedPrestamoId !== null
      ? prestamos.filter((prestamo) => prestamo.id_prestamo === selectedPrestamoId)
      : prestamos;

  if (loading) {
    return <FetchWithGif />;
  }

  return (
    <div className="space-y-6">
      <section className="ui-card px-6 py-7 md:px-8">
        <div>
          <h1 className="mt-2 flex items-center gap-3 text-2xl font-semibold text-slate-900 md:text-3xl">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-600 shadow-sm">
              <FaExclamationTriangle className="h-6 w-6" />
            </span>
            Gestión de moras
          </h1>
          <p className="mt-3 max-w-3xl text-sm text-slate-600">Seguimiento de préstamos en mora.</p>
        </div>
      </section>

      <GestionMorasMetricCards meta={meta} />

      {selectedPrestamo ? (
        <section className="ui-card border border-red-200 bg-red-50/70 px-5 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-600">Detalle del préstamo</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">
                Crédito #{selectedPrestamo.numero_credito} · {selectedPrestamo.cliente}
              </h2>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Mostrando 1 de {prestamos.length} préstamo{prestamos.length === 1 ? '' : 's'}
              </p>
            </div>

            <button
              type="button"
              onClick={handleVolverAlListado}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FaArrowLeft className="h-3.5 w-3.5" />
              Volver al listado
            </button>
          </div>
        </section>
      ) : null}

      <GestionMorasErrorState error={error} onRetry={() => cargarGestionMoras(selectedPrestamoId)} />

      {!error && !loading && prestamos.length === 0 ? (
        <GestionMorasEmptyState />
      ) : null}

      {!error && visiblePrestamos.length > 0 ? (
        <GestionMorasTable
          headers={PRESTAMO_HEADERS}
          prestamos={visiblePrestamos}
          expandedPrestamoId={selectedPrestamoId}
          onToggleExpanded={handleSeleccionarPrestamo}
          rowInteractionDisabled={Boolean(selectedPrestamo)}
          detailRowIdPrefix="detalle-mora"
          colSpan={6}
          renderRowCells={(prestamo) => {
            const restriccionCompromiso = obtenerRestriccionNuevoCompromiso(prestamo);

            return (
              <>
                <PrestamoClienteCell prestamo={prestamo} />
                <PrestamoNumeroCreditoCell prestamo={prestamo} />
                <PrestamoCurrencyCell value={prestamo.capital_pendiente} />
                <PrestamoCurrencyCell value={prestamo.total_mora} className="text-slate-900" />
                <PrestamoCurrencyCell value={prestamo.total_a_pagar} className="bg-red-50 text-red-700" pill />
                <td className="px-4 py-4 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        abrirModalCompromiso(prestamo);
                      }}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                      }}
                      disabled={restriccionCompromiso.bloqueado}
                      title={restriccionCompromiso.bloqueado ? restriccionCompromiso.mensaje : 'Registrar compromiso'}
                      className="inline-flex items-center rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-red-50"
                    >
                      Registrar compromiso
                    </button>

                    {prestamo.ultimo_compromiso_pago ? (
                      <span className="text-xs text-slate-500">
                        Último: {formatearFecha(prestamo.ultimo_compromiso_pago.fecha_compromiso)}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Sin registro</span>
                    )}

                    {restriccionCompromiso.bloqueado ? (
                      <span className="max-w-44 text-xs font-semibold text-amber-700">
                        {restriccionCompromiso.ayuda}
                      </span>
                    ) : null}
                  </div>
                </td>
              </>
            );
          }}
          renderExpandedContent={(prestamo) => (
            <>
              <UltimoCompromisoCard compromiso={prestamo.ultimo_compromiso_pago} />
              <CuotasVencidasTable cuotas={prestamo.cuotas} />
            </>
          )}
        />
      ) : null}

      <CompromisoPagoModal
        isOpen={Boolean(prestamoCompromiso)}
        prestamo={prestamoCompromiso}
        formData={compromisoForm}
        errors={compromisoErrors}
        saving={savingCompromiso}
        onClose={cerrarModalCompromiso}
        onChange={handleCompromisoChange}
        onSubmit={handleRegistrarCompromiso}
      />
    </div>
  );
};

export default GestionMorasUI;